import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../../../shared/lib/supabase'
import PortalSettingsPanel from '../../../components/PortalSettingsPanel'
import ClientUsersPanel from '../ClientUsersPanel'
import { useAssessment } from '../../../../../contexts/AssessmentContext'
import { CLIENT_STATUS_OPTIONS } from '../../../../../shared/utils/clientHelpers'
import { useRealtimeSubscription } from '../../../../../hooks/useRealtimeSubscription'
import { usePollingInterval } from '../../../../../hooks/usePollingInterval'

const subTabs = [
  {
    id: 'portal-settings',
    label: 'Portal Settings',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )
  },
  {
    id: 'portal-users',
    label: 'Portal Users',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  },
  {
    id: 'activity-feed',
    label: 'Activity Feed',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }
]

function AdminTab({ client, defaultSubTab }) {
  const [activeSubTab, setActiveSubTab] = useState(defaultSubTab || 'portal-settings')

  useEffect(() => {
    if (defaultSubTab) setActiveSubTab(defaultSubTab)
  }, [defaultSubTab])
  const [activities, setActivities] = useState([])
  const [loadingActivity, setLoadingActivity] = useState(true)

  const fetchActivity = useCallback(async () => {
    if (!client?.id) return
    setLoadingActivity(true)
    try {
      const since = new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString()
      const { data, error } = await supabase
        .from('audit_logs')
        .select('id, action, description, user_email, category, created_at, metadata')
        .eq('client_id', client.id)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error
      setActivities(data || [])
    } catch (err) {
      console.error('Error fetching client activity:', err)
    } finally {
      setLoadingActivity(false)
    }
  }, [client?.id])

  useEffect(() => {
    fetchActivity()
  }, [fetchActivity])

  // Realtime: prepend new entries (only when activity-feed tab is active)
  const isActivityTab = activeSubTab === 'activity-feed'
  useRealtimeSubscription({
    table: 'audit_logs',
    filter: client?.id ? `client_id=eq.${client.id}` : undefined,
    enabled: !!client?.id && isActivityTab,
    onInsert: (record) => {
      setActivities(prev => {
        if (prev.some(a => a.id === record.id)) return prev
        return [record, ...prev].slice(0, 20)
      })
    }
  })

  // 5-minute polling fallback (only when activity-feed tab is active)
  usePollingInterval(fetchActivity, 5 * 60 * 1000, !!client?.id && isActivityTab)

  const getCategoryColor = (category) => {
    switch (category) {
      case 'login': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
      case 'change': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
      case 'system': return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
    }
  }

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
          Portal configuration, user management, and activity monitoring
        </p>
      </div>

      {/* Sub-tab navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6">
          {subTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`pb-3 text-sm font-medium transition-colors relative ${
                activeSubTab === tab.id
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
              {activeSubTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Sub-tab content */}
      {activeSubTab === 'portal-settings' && (
        <>
          <EngagementStatusCard client={client} />
          <PortalSettingsPanel client={client} />
        </>
      )}

      {activeSubTab === 'portal-users' && (
        <ClientUsersPanel client={client} />
      )}

      {activeSubTab === 'activity-feed' && (
        <div className="card p-6">
          {loadingActivity ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
              <svg className="w-10 h-10 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">No activity recorded for this client yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map(activity => (
                <div key={activity.id} className="flex items-start gap-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <span className={`flex-shrink-0 mt-0.5 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase ${getCategoryColor(activity.category)}`}>
                    {activity.category}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-white">
                      {activity.description}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {activity.user_email && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">{activity.user_email}</span>
                      )}
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(activity.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function EngagementStatusCard({ client }) {
  const { updateClient } = useAssessment()
  const [saving, setSaving] = useState(false)

  const portalSettings = client.portalSettings || {}
  const portalEnabled = portalSettings.portalEnabled !== false

  const handleStatusChange = async (newStatus) => {
    await updateClient(client.id, { status: newStatus })
  }

  const handlePortalToggle = async () => {
    setSaving(true)
    const updated = { ...portalSettings, portalEnabled: !portalEnabled }
    await updateClient(client.id, { portalSettings: updated })
    setSaving(false)
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 mb-6">
      <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Engagement Status</h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Controls the client's engagement phase and portal experience.</p>
      </div>

      <div className="flex items-center gap-6">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Engagement Phase</p>
          <select
            value={client.status || 'discovery'}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="pl-3 pr-8 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {CLIENT_STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="border-l border-gray-200 dark:border-gray-700 pl-6">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Client Portal</p>
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={portalEnabled}
                onChange={handlePortalToggle}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-green-500"></div>
            </label>
            <span className={`text-xs font-medium ${portalEnabled ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {portalEnabled ? 'Active' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminTab
